import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // Enable logs to be sent to Sentry
  enableLogs: true,
});
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { PublicRoute } from "./components/auth/PublicRoute";
import { ScrollToTop } from "./components/ScrollToTop";
import { ErrorBoundary, AuthErrorBoundary } from "./components/ErrorBoundary";
import { SecurityProvider } from "./components/SecurityProvider";
import { WhatsAppFloatButton } from "./components/common";
import { Home } from "./screens/Home";
import { AuthPage } from "./screens/Auth";
import { SettingsPage } from "./screens/Settings";
import { UnderDevelopmentPage } from "./screens/UnderDevelopment";
import { LessonPlanBuilderPage } from "./screens/LessonPlanBuilder";
import { AssessmentBuilderPage } from "./screens/AssessmentBuilder";
import { GamificationPage } from "./screens/Gamification";
import { HistoryPage } from "./screens/History";
import { LessonPlanViewerPage } from "./screens/LessonPlanBuilder";
import { AssessmentViewerPage } from "./screens/AssessmentBuilder";
import { CollaborationPage } from "./screens/Collaboration";
import { ResetPasswordPage } from "./screens/ResetPassword/";

createRoot(document.getElementById("app") as HTMLElement).render(
  <StrictMode>
    <ErrorBoundary>
      <SecurityProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ScrollToTop />
          <AuthErrorBoundary>
            <AuthProvider>
              <NotificationProvider>
                <Routes>
                  <Route
                    path="/auth"
                    element={
                      <PublicRoute>
                        <AuthPage />
                      </PublicRoute>
                    }
                  />

                  <Route
                    path="/reset-password"
                    element={<ResetPasswordPage />}
                  />

                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Home />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <SettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/lesson-plan-builder"
                    element={
                      <ProtectedRoute>
                        <LessonPlanBuilderPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/assessment-builder"
                    element={
                      <ProtectedRoute>
                        <AssessmentBuilderPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gamification"
                    element={
                      <ProtectedRoute>
                        <GamificationPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/history"
                    element={
                      <ProtectedRoute>
                        <HistoryPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route
                  path="/collaboration"
                  element={<Navigate to="/development/collaboration" replace />}
                /> */}
                  <Route
                    path="/collaboration"
                    element={
                      <ProtectedRoute>
                        <CollaborationPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/development/:featureName"
                    element={
                      <ProtectedRoute>
                        <UnderDevelopmentPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/lesson/:id"
                    element={
                      <ProtectedRoute>
                        <LessonPlanViewerPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/assessment/:id"
                    element={
                      <ProtectedRoute>
                        <AssessmentViewerPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/"
                    element={<Navigate to="/dashboard" replace />}
                  />
                </Routes>
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: "#363636",
                      color: "#fff",
                    },
                    success: {
                      style: {
                        background: "#10b981",
                      },
                    },
                    error: {
                      style: {
                        background: "#ef4444",
                      },
                    },
                  }}
                />
                <WhatsAppFloatButton />
              </NotificationProvider>
            </AuthProvider>
          </AuthErrorBoundary>
        </BrowserRouter>
      </SecurityProvider>
    </ErrorBoundary>
  </StrictMode>
);
